import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customMbaEnquiriesAPI } from '../../utils/api';
import * as XLSX from 'xlsx';
import '../../styles/AdminTable.scss';

const AdminCustomMbaEnquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters & Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    fetchEnquiries();
  }, [page, status, fromDate, toDate]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page,
        limit,
        search: search.trim() || undefined,
        status: status || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined
      };
      
      const response = await customMbaEnquiriesAPI.getAll(params);
      if (response.data?.success) {
        setEnquiries(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        setError('Failed to fetch MBA project enquiries');
      }
    } catch (err) {
      setError('Error loading MBA project enquiries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEnquiries();
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatus('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this enquiry?')) {
      try {
        setLoading(true);
        const response = await customMbaEnquiriesAPI.delete(id);
        if (response.data?.success) {
          alert('Enquiry deleted successfully.');
          fetchEnquiries();
        } else {
          setError('Failed to delete enquiry.');
        }
      } catch (err) {
        setError('Error deleting enquiry.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const exportToExcel = () => {
    if (enquiries.length === 0) {
      alert('No data available to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(
      enquiries.map(e => ({
        'Enquiry ID': e.enquiry_id,
        'Client Name': e.full_name,
        'Email': e.email,
        'Mobile': e.mobile,
        'College': e.college,
        'University': e.university,
        'Project Title': e.project_title,
        'Specialization': e.specialization,
        'Status': e.status,
        'Date': new Date(e.created_at).toLocaleString()
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MBA_Enquiries");
    XLSX.writeFile(wb, `MBA_Project_Enquiries_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const getStatusColor = (s) => {
    const colors = {
      Pending: '#eab308',
      Reviewed: '#3b82f6',
      Shortlisted: '#a855f7',
      Selected: '#10b981',
      Rejected: '#ef4444'
    };
    return colors[s] || '#64748b';
  };

  return (
    <div className="admin-table-container">
      <div className="table-header">
        <div>
          <h2>MBA Project Enquiries</h2>
          <p className="subtitle">Total enquiries found: <strong>{total}</strong></p>
        </div>
        <button className="btn-primary" onClick={exportToExcel} style={{ background: '#10b981' }}>
          Export to Excel
        </button>
      </div>

      {error && <div className="error-message" style={{ background: '#fef2f2', color: '#ef4444', padding: '10px', borderRadius: '6px', marginBottom: '15px' }}>{error}</div>}

      {/* Filters */}
      <div className="filters-card" style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <form onSubmit={handleSearchSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Search</label>
            <input 
              type="text" 
              placeholder="Name, email, college, ID..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Status</label>
            <select 
              value={status} 
              onChange={(e) => setStatus(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Reviewed">Reviewed</option>
              <option value="Shortlisted">Shortlisted</option>
              <option value="Selected">Selected</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>From Date</label>
            <input 
              type="date" 
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>To Date</label>
            <input 
              type="date" 
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn-primary" style={{ padding: '9px 15px', flex: 1 }}>Search</button>
            <button type="button" onClick={handleResetFilters} className="btn-secondary" style={{ padding: '9px 15px', color: '#475569', borderColor: '#cbd5e1' }}>Reset</button>
          </div>
        </form>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading enquiries...</div>
      ) : (
        <>
          <div className="table-responsive" style={{ background: '#ffffff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: '600' }}>
                  <th style={{ padding: '15px' }}>Enquiry ID</th>
                  <th style={{ padding: '15px' }}>Client / Contact</th>
                  <th style={{ padding: '15px' }}>College / University</th>
                  <th style={{ padding: '15px' }}>Project Title</th>
                  <th style={{ padding: '15px' }}>Specialization</th>
                  <th style={{ padding: '15px' }}>Status</th>
                  <th style={{ padding: '15px' }}>Date</th>
                  <th style={{ padding: '15px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No MBA enquiries found.</td>
                  </tr>
                ) : (
                  enquiries.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '15px', fontWeight: '600', color: '#0f172a' }}>{item.enquiry_id}</td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ fontWeight: '500', color: '#0f172a' }}>{item.full_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{item.email} | {item.mobile}</div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ color: '#334155' }}>{item.college}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{item.university}</div>
                      </td>
                      <td style={{ padding: '15px', color: '#334155', fontWeight: '500' }}>
                        {item.project_title}
                      </td>
                      <td style={{ padding: '15px', color: '#0f172a', fontWeight: '500' }}>
                        {item.specialization}
                      </td>
                      <td style={{ padding: '15px' }}>
                        <span style={{
                          background: getStatusColor(item.status) + '15',
                          color: getStatusColor(item.status),
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'inline-block'
                        }}>
                          {item.status}
                        </span>
                      </td>
                      <td style={{ padding: '15px', color: '#64748b', fontSize: '14px' }}>
                        {new Date(item.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'right' }}>
                        <button 
                          className="btn-action"
                          onClick={() => navigate(`/admin/custom-mba-enquiries/${item.id}`)}
                          style={{ marginRight: '8px', color: '#3b82f6', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                        >
                          View
                        </button>
                        <button 
                          className="btn-action"
                          onClick={() => handleDelete(item.id)}
                          style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '15px' }}>
              <button 
                disabled={page === 1} 
                onClick={() => setPage(page - 1)}
                className="btn-secondary"
                style={{ padding: '6px 12px' }}
              >
                Previous
              </button>
              <span style={{ fontSize: '14px', color: '#475569' }}>Page <strong>{page}</strong> of <strong>{Math.ceil(total / limit)}</strong></span>
              <button 
                disabled={page * limit >= total} 
                onClick={() => setPage(page + 1)}
                className="btn-secondary"
                style={{ padding: '6px 12px' }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminCustomMbaEnquiries;
